import React, { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { Set } from 'immutable';

import {
  approvePaymentAction,
  deletePaymentAction,
  payPaymentAction,
  recreatePaymentAction,
  resetPaymentProcessAction,
  startPaymentApprovalFlowAction,
  updatePaymentTagsAction,
  voidPaymentAction,
} from 'domain/payments';
import { PAY_IS_CONTROLLER, PAY_IS_OWNER } from 'domain/restriction';
import { promisify } from 'lib/helpers';

import Button from '@mui/material/Button';
import RouterLink from 'components/mui/Router/RouterLink';

import { PaymentContext } from 'pages/dokkaPay/pages/payment/PaymentContext';

import { PAYMENT_STATUSES, PAYMENT_TAGS } from 'domain/payments/constants';
import type { PaymentInfoRecord } from 'domain/payments/types';
import { canStartPaymentApprovalFlow, canPayPayment } from './helpers';
import PayButton from './components/PayButton';

interface UsePaymentActionsParams {
  paymentInfo: PaymentInfoRecord;
  isGranted: (policy: unknown) => boolean;
  handleOpenRejectDialog: () => void;
  shouldCurrentUserApprove: boolean;
  paymentTags: Set<string>;
  isApprovalRequired: boolean;
}

const usePaymentActions = ({
  paymentInfo,
  isGranted,
  handleOpenRejectDialog,
  shouldCurrentUserApprove,
  paymentTags,
  isApprovalRequired,
}: UsePaymentActionsParams) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { backLink } = useContext(PaymentContext);
  const { formatMessage } = useIntl();

  const handleStartApprovalFlow = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.sendToApproval.title',
        defaultMessage: 'Send payment to approval',
      }),
      description: formatMessage({
        id: 'payment.confirmation.sendToApproval.placeholder',
        defaultMessage: 'Are you sure?',
      }),
      confirmationText: formatMessage({
        id: 'payment.footer.actionButton.sendToApproval',
        defaultMessage: 'Send to approval',
      }),
    })
      .then(() => {
        dispatch(startPaymentApprovalFlowAction({ id: paymentInfo.id }) as any);
      })
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage]);

  const handleApprove = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.sendToApproval.title',
        defaultMessage: 'Send payment to approval',
      }),
      description: formatMessage({
        id: 'payment.confirmation.sendToApproval.placeholder',
        defaultMessage: 'Are you sure?',
      }),
      confirmationText: formatMessage({
        id: 'payment.footer.actionButton.approve',
        defaultMessage: 'Approve',
      }),
    })
      .then(() => {
        dispatch(approvePaymentAction({ id: paymentInfo.id }) as any);
      })
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage]);

  const handleResetProcess = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.resetProcess.title',
        defaultMessage: 'Reset the process',
      }),
      description: formatMessage({
        id: 'payment.confirmation.resetProcess.placeholder',
        defaultMessage: 'Are you sure?',
      }),
      confirmationText: formatMessage({
        id: 'payment.footer.actionButton.resetProcess',
        defaultMessage: 'Reset the process',
      }),
    })
      .then(() => {
        dispatch(resetPaymentProcessAction({ id: paymentInfo.id }) as any);
      })
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage]);

  const handlePay = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.pay.title',
        defaultMessage: 'Pay payment',
      }),
      description: formatMessage({
        id: 'payment.confirmation.pay.placeholder',
        defaultMessage: 'Are you sure?',
      }),
      confirmationText: formatMessage(
        { id: 'payment.footer.actionButton.pay', defaultMessage: `Pay ${paymentInfo.totalAmount}` },
        { sum: paymentInfo.totalAmount },
      ),
    })
      .then(() => {
        dispatch(payPaymentAction({ id: paymentInfo.id }) as any);
      })
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage, paymentInfo.totalAmount]);

  const handleVoid = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.void.title',
        defaultMessage: 'Void payment',
      }),
      description: formatMessage({
        id: 'payment.confirmation.void.placeholder',
        defaultMessage: 'Are you sure?',
      }),
      confirmationText: formatMessage({
        id: 'payment.footer.actionButton.void',
        defaultMessage: 'Void',
      }),
      confirmationButtonProps: {
        color: 'error',
      },
    })
      .then(() => {
        dispatch(voidPaymentAction({ id: paymentInfo.id }) as any);
      })
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage]);

  const handleCreatePayment = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.recreatePayment.title',
        defaultMessage: 'Create new payment',
      }),
      description: formatMessage({
        id: 'payment.confirmation.recreatePayment.placeholder',
        defaultMessage: 'Are you sure? A new payment will be created based on this one.',
      }),
      confirmationText: formatMessage({
        id: 'payment.footer.actionButton.recreatePayment',
        defaultMessage: 'Create payment',
      }),
    })
      .then(() => {
        dispatch(recreatePaymentAction({ id: paymentInfo.id }) as any);
      })
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage]);

  // the payment is gone once deleted, so leave the detail page for the grid we came from
  const handleDelete = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'payment.confirmation.delete.title',
        defaultMessage: 'Delete payment',
      }),
      description: formatMessage({
        id: 'payment.confirmation.delete.placeholder',
        defaultMessage: 'Are you sure?',
      }),
      confirmationText: formatMessage({
        id: 'button.delete',
        defaultMessage: 'Delete',
      }),
      confirmationButtonProps: {
        color: 'error',
      },
    })
      .then(() =>
        promisify((arg: Parameters<typeof deletePaymentAction>[0]) => dispatch(deletePaymentAction(arg)), {
          id: paymentInfo.id,
        }).then(() => {
          navigate(backLink);
        }),
      )
      .catch(() => {});
  }, [dispatch, paymentInfo.id, confirm, formatMessage, navigate, backLink]);

  const updateTag = useCallback(
    (tag: string, isActive = true) => {
      dispatch(updatePaymentTagsAction({ id: paymentInfo.id, tag, isActive }) as any);
    },
    [dispatch, paymentInfo.id],
  );

  const handleArchive = useCallback(() => {
    updateTag(PAYMENT_TAGS.archive);
  }, [updateTag]);

  const handleUnarchive = useCallback(() => {
    updateTag(PAYMENT_TAGS.archive, false);
  }, [updateTag]);

  const handleSaveForLater = useCallback(() => {
    updateTag(PAYMENT_TAGS.saveForLater);
  }, [updateTag]);

  const handleStartPaymentProcess = useCallback(() => {
    updateTag(PAYMENT_TAGS.saveForLater, false);
  }, [updateTag]);

  const isVisibleResetProcessBtn =
    (isGranted(PAY_IS_OWNER) || isGranted(PAY_IS_CONTROLLER)) &&
    (paymentInfo.status === PAYMENT_STATUSES.PENDING_APPROVAL ||
      paymentInfo.status === PAYMENT_STATUSES.APPROVED ||
      paymentInfo.status === PAYMENT_STATUSES.APPROVAL_REJECTED);

  const isOwnerOrController = isGranted(PAY_IS_OWNER) || isGranted(PAY_IS_CONTROLLER);

  const getActionButtons = (): React.ReactNode => {
    switch (paymentInfo.status) {
      case PAYMENT_STATUSES.PREPARATION:
        if (!isOwnerOrController) return null;
        return isApprovalRequired ? (
          <Button
            variant="contained"
            onClick={handleStartApprovalFlow}
            disabled={!canStartPaymentApprovalFlow(paymentInfo)}
          >
            {formatMessage({ id: 'payment.footer.actionButton.sendToApproval', defaultMessage: 'Send to approval' })}
          </Button>
        ) : (
          <PayButton onClick={handlePay} totalAmount={paymentInfo.totalAmount} disabled={!canPayPayment(paymentInfo)} />
        );
      case PAYMENT_STATUSES.PENDING_APPROVAL:
        return shouldCurrentUserApprove ? (
          <>
            <Button variant="outlined" color="error" onClick={handleOpenRejectDialog}>
              {formatMessage({ id: 'payment.footer.actionButton.reject', defaultMessage: 'Reject' })}
            </Button>
            <Button variant="contained" onClick={handleApprove}>
              {formatMessage({ id: 'payment.footer.actionButton.approve', defaultMessage: 'Approve' })}
            </Button>
          </>
        ) : null;
      case PAYMENT_STATUSES.APPROVED:
        return (
          isOwnerOrController && (
            <PayButton onClick={handlePay} totalAmount={paymentInfo.totalAmount} disabled={!canPayPayment(paymentInfo)} />
          )
        );
      case PAYMENT_STATUSES.PROCESSING:
        return isOwnerOrController ? (
          <Button variant="contained" color="error" onClick={handleVoid}>
            {formatMessage({ id: 'payment.footer.actionButton.void', defaultMessage: 'Void' })}
          </Button>
        ) : null;
      case PAYMENT_STATUSES.APPROVAL_REJECTED:
      case PAYMENT_STATUSES.PROCESSING_VOID:
      case PAYMENT_STATUSES.DECLINED:
        return (
          <Button variant="contained" component={RouterLink as any} to={backLink as any}>
            {formatMessage({ id: 'button.ok', defaultMessage: 'Ok' })}
          </Button>
        );
      case PAYMENT_STATUSES.PAID:
        return !paymentTags.includes(PAYMENT_TAGS.archive) && isOwnerOrController ? (
          <Button variant="contained" onClick={handleArchive}>
            {formatMessage({ id: 'payment.footer.actionButton.archive', defaultMessage: 'Archive' })}
          </Button>
        ) : null;
      case PAYMENT_STATUSES.VOIDED:
        return (
          isOwnerOrController && (
            <Button
              variant="contained"
              onClick={handleCreatePayment}
              disabled={paymentTags.includes(PAYMENT_TAGS.hasRecreatedPayment)}
            >
              {formatMessage({ id: 'payment.footer.actionButton.recreatePayment', defaultMessage: 'Create payment' })}
            </Button>
          )
        );
      default:
        return null;
    }
  };

  return {
    handleStartApprovalFlow,
    handleApprove,
    handleResetProcess,
    isVisibleResetProcessBtn,
    getActionButtons,
    handleArchive,
    handleUnarchive,
    handleSaveForLater,
    handleStartPaymentProcess,
    handleDelete,
  };
};

export default usePaymentActions;
